'use client';

import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
  Checkbox,
  Button,
  ButtonGroup,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  ExpandLess,
  ExpandMore,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
} from '@mui/icons-material';
import { getStockSentiment, getPortfolios, getMoneyFlowHealth, getSystemHealth, warmPortfolioCache, warmMarketCache, SystemHealth } from '../../lib/api';
import { formatDataTimestamp, formatCacheTimestamp } from '../../lib/timezone';

interface MoneyFlowIndicators {
  symbol: string;
  obv: number;
  cmf: number;
  trend: 'ACCUMULATION' | 'DISTRIBUTION'; // CMF >= 0 = ACCUMULATION, CMF < 0 = DISTRIBUTION
  timestamp: string;
}

interface StockSentiment {
  stocks: StockAnalysis[];
  timestamp: string;
  cached?: boolean;
  cacheStatus?: 'HIT' | 'MISS' | 'STALE_REFRESH'; // v3.4.5: DO-only cache (v3.3.0+)
  cachedAt?: string;
  missDetails?: 'L1_AND_L2' | 'L1_ONLY';
}

interface StockAnalysis {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  gptContext: string;
  distilbertSentiment: number; // -1 to +1
  sentimentLabel: 'BEARISH' | 'NEUTRAL' | 'BULLISH';
  agreement: 'AGREE' | 'PARTIAL' | 'DISAGREE';
  signal: 'STRONG_BUY' | 'OPPORTUNITY' | 'BULL_TRAP' | 'STRONG_SELL';
  confidence: number; // 0-100%
  moneyFlow: MoneyFlowIndicators | null;
  newsCount: number;
}

interface Portfolio {
  id: string;
  name: string;
  description: string;
  symbols: string[];
  createdAt: string;
  updatedAt: string;
}

export function QuadrantChartWidget() {
  
  const [sentiment, setSentiment] = useState<StockSentiment | null>(null);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);
  const [portfoliosLoading, setPortfoliosLoading] = useState(true);
  const [sentimentLoading, setSentimentLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('Loading...'); // Dynamic loading message
  const [error, setError] = useState<string | null>(null);
  const [expandedStock, setExpandedStock] = useState<string | null>(null);
  const [visibleStocks, setVisibleStocks] = useState<StockAnalysis[]>([]); // Stocks shown progressively
  const [loadingProgress, setLoadingProgress] = useState(0); // Loading progress (0-100)
  const [isStale, setIsStale] = useState(false); // Stale cache indicator
  const [selectedStocks, setSelectedStocks] = useState<Set<string>>(new Set()); // Selected stock symbols for chart display
  const [moneyFlowHealth, setMoneyFlowHealth] = useState<any>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [hideStocksWithoutMoneyFlow, setHideStocksWithoutMoneyFlow] = useState(false); // Filter preference
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null); // Data freshness tracking
  const [isWarming, setIsWarming] = useState(false);
  const [warmMessage, setWarmMessage] = useState<string | null>(null);

  const normalizeStockSentiment = (data: any): StockSentiment => {
    const stocks = Array.isArray(data?.stocks) ? data.stocks : [];
    return {
      stocks: stocks.map((stock: any) => ({
        ...stock,
        signal: stock.signal ?? stock.finalSignal ?? 'STRONG_BUY',
        change: stock.change ?? stock.priceChange ?? 0,
        changePercent: stock.changePercent ?? stock.priceChangePercent ?? 0,
        newsCount: stock.newsCount ?? stock.articles?.length ?? 0,
        moneyFlow: stock.moneyFlow ?? null,
      })),
      timestamp: data?.timestamp ?? new Date().toISOString(),
      cached: data?.cached ?? false,
      cacheStatus: data?.cacheStatus,
      cachedAt: data?.cachedAt,
      missDetails: data?.missDetails,
    };
  };

  // Fetch portfolios on mount (FAST - from KV cache)
  useEffect(() => {
    async function fetchPortfolios() {
      try {
        setPortfoliosLoading(true);
        const data = await getPortfolios();
        const portfolioList = (Array.isArray(data) ? data : []) as Portfolio[];
        setPortfolios(portfolioList);

        // Try to load saved portfolio from localStorage
        const savedPortfolioId = localStorage.getItem('dac_selected_portfolio_id');
        let portfolioToSelect: Portfolio | null = null;

        if (savedPortfolioId) {
          // Find the saved portfolio in the list
          portfolioToSelect = portfolioList.find((p: any) => p.id === savedPortfolioId) as Portfolio;
        }

        // Fallback: Select active portfolio or first portfolio
        if (!portfolioToSelect) {
          portfolioToSelect =
            (portfolioList.find((p: any) => p.isActive) ||
              portfolioList.find((p: any) => p.id?.includes?.('active')) ||
              null) ?? null;
        }

        if (!portfolioToSelect && portfolioList.length > 0) {
          portfolioToSelect = portfolioList[0];
        }

        if (portfolioToSelect) {
          setSelectedPortfolio(portfolioToSelect);
        }
      } catch (err) {
        setError('Failed to load portfolios');
      } finally {
        setPortfoliosLoading(false);
      }
    }

    fetchPortfolios();
  }, []);

  // Fetch sentiment when portfolio changes (SLOW - external APIs + AI)
  useEffect(() => {
    if (!selectedPortfolio) {
      return;
    }

    async function fetchData() {
      const startTime = Date.now();
      let elapsedInterval: NodeJS.Timeout | null = null;

      try {
        setSentimentLoading(true);
        setLoadingMessage('Checking cache...');
        setError(null);
        setVisibleStocks([]); // Reset visible stocks
        setLoadingProgress(0);

        // Update elapsed time every second after 2s
        const slowTimer = setTimeout(() => {
          setLoadingMessage('Cache miss - Fetching from API...');
          elapsedInterval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            setLoadingMessage(`Analyzing stocks... (${elapsed}s)`);
          }, 1000);
        }, 2000);

        // Fetch health status in parallel
        setHealthLoading(true);
        const [data, healthData, sysHealthData] = await Promise.allSettled([
          getStockSentiment(selectedPortfolio!.id),
          getMoneyFlowHealth().catch(() => null), // Health API is optional
          getSystemHealth().catch(() => null), // System Health is optional
        ]);

        clearTimeout(slowTimer);
        if (elapsedInterval) clearInterval(elapsedInterval);

        const sentimentData = data.status === 'fulfilled' ? data.value : null;

        // Update loading message based on cache status
        const cacheStatus = (sentimentData as any)?.cacheStatus;
        if (cacheStatus === 'HIT') {
          setLoadingMessage('Loaded from cache');
        } else {
          setLoadingMessage('Loaded from API');
        }

        const healthStatus = healthData.status === 'fulfilled' ? healthData.value : null;
        const sysHealth = sysHealthData.status === 'fulfilled' ? sysHealthData.value : null;
        setMoneyFlowHealth(healthStatus);
        setSystemHealth(sysHealth);
        setHealthLoading(false);

        // Check if stale cache was served
        const staleFlag = (sentimentData as any)?._stale === true;
        setIsStale(staleFlag);

        // Set last refreshed timestamp
        setLastRefreshed(new Date().toISOString());

        if (!sentimentData) {
          setSentiment(null);
          setError('Failed to load portfolio sentiment data');
          return; // Exit early on error
        }

        // Normalize and set sentiment data
        const normalized = normalizeStockSentiment(sentimentData);
        setSentiment(normalized);

        // Set all stocks visible immediately (no progressive reveal)
        const stocks = normalized.stocks || [];
        setVisibleStocks(stocks);
        setLoadingProgress(100);

        // Select all stocks by default
        if (stocks.length > 0) {
          const allSymbols = new Set<string>(stocks.map((s: StockAnalysis) => s.symbol));
          setSelectedStocks(allSymbols);
        }

        // Auto-hide stale indicator after 3 seconds
        if (staleFlag) {
          setTimeout(() => setIsStale(false), 3000);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch stock sentiment');
      } finally {
        setSentimentLoading(false);
        setLoadingProgress(100);
      }
    }

    fetchData();
  }, [selectedPortfolio]);

  const handlePortfolioChange = (event: SelectChangeEvent) => {
    const portfolio = portfolios.find((p) => p.id === event.target.value);
    if (portfolio) {
      setSelectedPortfolio(portfolio);
      // Save selected portfolio to localStorage
      localStorage.setItem('dac_selected_portfolio_id', portfolio.id);
    }
  };

  const handleStockClick = (symbol: string) => {
    setExpandedStock(expandedStock === symbol ? null : symbol);
  };

  const handleStockToggle = (symbol: string) => {
    setSelectedStocks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(symbol)) {
        newSet.delete(symbol);
      } else {
        newSet.add(symbol);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allSymbols = new Set(filteredStocks.map((s) => s.symbol));
    setSelectedStocks(allSymbols);
  };

  const handleSelectNone = () => {
    setSelectedStocks(new Set());
  };

  // Check if current user is admin (simple environment variable check)
  const isAdmin = import.meta.env.VITE_ADMIN_USER === 'true' ||
                  localStorage.getItem('dac_admin_user') === 'true';

  const handleRetryFetch = async () => {
    if (!selectedPortfolio) return;

    try {
      setSentimentLoading(true);
      setError(null);
      setWarmMessage('Refreshing portfolio data...');

      const data = await getStockSentiment(selectedPortfolio.id);
      const normalized = normalizeStockSentiment(data);
      setSentiment(normalized);
      setLastRefreshed(new Date().toISOString());

      // Clear any existing visible stocks and re-reveal
      setVisibleStocks([]);
      const stocks = normalized.stocks || [];
      if (stocks && stocks.length > 0) {
        // Select all stocks by default
        const allSymbols = new Set(stocks.map(s => s.symbol));
        setSelectedStocks(allSymbols);

        // Progressive reveal
        for (let i = 0; i < stocks.length; i++) {
          await new Promise((resolve) => setTimeout(resolve, 100)); // Faster refresh
          setVisibleStocks((prev) => [...prev, stocks[i]]);
          setLoadingProgress(Math.round(((i + 1) / stocks.length) * 100));
        }
      }

      setWarmMessage('Data refreshed successfully');
      setTimeout(() => setWarmMessage(null), 3000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh data');
      setWarmMessage('Refresh failed');
      setTimeout(() => setWarmMessage(null), 3000);
    } finally {
      setSentimentLoading(false);
    }
  };

  const handleWarmCache = async () => {
    if (!selectedPortfolio || !isAdmin) return;

    try {
      setIsWarming(true);
      setWarmMessage('Warming cache...');

      // Warm both market and current portfolio
      const results = await Promise.allSettled([
        warmPortfolioCache(selectedPortfolio.id),
        warmMarketCache(),
      ]);

      const allSuccessful = results.every(r => r.status === 'fulfilled');
      const message = allSuccessful ? 'Cache warming completed successfully' : 'Cache warming completed with some issues';

      setWarmMessage(message);
      setTimeout(() => setWarmMessage(null), 5000);

      // After warming, refresh the data
      await handleRetryFetch();

    } catch (err) {
      setWarmMessage('Cache warming failed');
      setTimeout(() => setWarmMessage(null), 5000);
    } finally {
      setIsWarming(false);
    }
  };

  const getQuadrant = (stock: StockAnalysis): { name: string; color: string; quadrant: number } | null => {
    if (!stock.moneyFlow) return null;

    const newsBullish = stock.distilbertSentiment > 0.2;
    const newsBearish = stock.distilbertSentiment < -0.2;
    const moneyBullish = stock.moneyFlow.trend === 'ACCUMULATION';
    const moneyBearish = stock.moneyFlow.trend === 'DISTRIBUTION';

    // QUADRANT I: STRONG_BUY (top-right)
    if (newsBullish && moneyBullish) {
      return { name: 'STRONG_BUY', color: '#4caf50', quadrant: 1 };
    }

    // QUADRANT II: OPPORTUNITY (top-left)
    if (newsBearish && moneyBullish) {
      return { name: 'OPPORTUNITY', color: '#2196f3', quadrant: 2 };
    }

    // QUADRANT III: STRONG_SELL (bottom-left)
    if (newsBearish && moneyBearish) {
      return { name: 'STRONG_SELL', color: '#f44336', quadrant: 3 };
    }

    // QUADRANT IV: BULL_TRAP (bottom-right)
    if (newsBullish && moneyBearish) {
      return { name: 'BULL_TRAP', color: '#ff9800', quadrant: 4 };
    }

    return null;
  };

  // Show loading only if portfolios are still loading (first load)
  if (portfoliosLoading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="caption" sx={{ display: 'block', mt: 2 }}>
              Loading portfolios...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Show error if portfolios failed to load
  if (error && !selectedPortfolio) {
    return (
      <Card data-testid="quadrant-chart-error">
        <CardContent>
          <Alert severity="error" icon={<ErrorIcon />} data-testid="error-alert">
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Chart dimensions - responsive
  const chartWidth = 800;
  const chartHeight = 600;
  const padding = 80;

  // CMF scaling: Industry standard range is -0.5 to +0.5 (typical fluctuation)
  // Theoretical range is -1 to +1, but CMF rarely exceeds ±0.5 in practice
  const CMF_DISPLAY_MIN = -0.5;
  const CMF_DISPLAY_MAX = 0.5;

  // Filter stocks based on user preference and money flow availability
  const filteredStocks = hideStocksWithoutMoneyFlow
    ? visibleStocks.filter((s) => s.moneyFlow !== null)
    : visibleStocks;

  const stocksWithMoneyFlow = filteredStocks.filter((s) => s.moneyFlow !== null);
  const stocksWithoutMoneyFlow = filteredStocks.filter((s) => s.moneyFlow === null);

  // Create placeholder stock list from portfolio symbols (shown while loading)
  const portfolioStocks = selectedPortfolio?.symbols.map((symbol) => ({
    symbol,
    name: 'Loading...',
    loading: true,
  })) || [];

  // Show loading indicator only while fetching
  const isRevealing = sentimentLoading;

  return (
    <Card data-testid="quadrant-chart-widget">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" component="div">
              Portfolio 4-Quadrant Analysis
            </Typography>
            {/* Stale cache indicator */}
            {isStale && (
              <Chip
                label="Refreshing..."
                size="small"
                color="info"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                }}
              />
            )}
          </Box>

          {/* Portfolio Selector and Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select data-testid="portfolio-selector" value={selectedPortfolio?.id || ''} onChange={handlePortfolioChange} displayEmpty>
                {portfolios.map((portfolio) => (
                  <MenuItem key={portfolio.id} value={portfolio.id}>
                    {portfolio.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Admin Controls */}
            {(isAdmin || stocksWithMoneyFlow.length === 0) && (
              <ButtonGroup size="small" variant="outlined">
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={handleRetryFetch}
                  disabled={sentimentLoading || !selectedPortfolio}
                  title="Refresh portfolio data"
                >
                  Retry
                </Button>
                {isAdmin && (
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={handleWarmCache}
                    disabled={isWarming || sentimentLoading || !selectedPortfolio}
                    title="Warm cache (admin only)"
                    color="warning"
                  >
                    Warm
                  </Button>
                )}
              </ButtonGroup>
            )}
          </Box>
        </Box>

        {/* Status Messages */}
        {warmMessage && (
          <Box sx={{ mb: 2 }}>
            <Alert severity={warmMessage.includes('success') ? 'success' : warmMessage.includes('failed') ? 'error' : 'info'}>
              {warmMessage}
            </Alert>
          </Box>
        )}

        {/* Quadrant Legend */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#4caf50', borderRadius: 1 }} />
            <Typography variant="caption">
              <strong>STRONG_BUY</strong> (Q1): News bullish + Money accumulation
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#2196f3', borderRadius: 1 }} />
            <Typography variant="caption">
              <strong>OPPORTUNITY</strong> (Q2): News bearish + Money accumulation
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#f44336', borderRadius: 1 }} />
            <Typography variant="caption">
              <strong>STRONG_SELL</strong> (Q3): News bearish + Money distribution
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#ff9800', borderRadius: 1 }} />
            <Typography variant="caption">
              <strong>BULL_TRAP</strong> (Q4): News bullish + Money distribution
            </Typography>
          </Box>
        </Box>

        {/* Filter Controls */}
        {stocksWithoutMoneyFlow.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredStocks.length} stock{filteredStocks.length !== 1 ? 's' : ''} shown
              {stocksWithoutMoneyFlow.length > 0 && ` (${stocksWithoutMoneyFlow.length} without money flow)`}
            </Typography>
            <Tooltip
              title={hideStocksWithoutMoneyFlow
                ? "Show all stocks including those without money flow data"
                : "Hide stocks without money flow data to focus on complete analysis"}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={hideStocksWithoutMoneyFlow}
                    onChange={(e) => setHideStocksWithoutMoneyFlow(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption">
                    Hide incomplete data
                  </Typography>
                }
              />
            </Tooltip>
          </Box>
        )}

        {/* Stock List + Chart Layout */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, my: 3 }}>
          {/* Stock List Sidebar */}
          <Box
            sx={{
              width: { xs: '100%', md: 300 },
              maxHeight: chartHeight,
              overflow: 'auto',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 1,
              backgroundColor: 'rgba(0,0,0,0.2)',
            }}
          >
            {/* Select All/None buttons */}
            <Box sx={{ p: 1, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
              <ButtonGroup size="small" fullWidth>
                <Button onClick={handleSelectAll} variant="outlined">
                  Select All
                </Button>
                <Button onClick={handleSelectNone} variant="outlined">
                  Select None
                </Button>
              </ButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                {selectedStocks.size} of {visibleStocks.length} selected
              </Typography>
            </Box>

            <List dense disablePadding>
              {/* Show loading progress */}
              {isRevealing && (
                <ListItem>
                  <Box sx={{ width: '100%', p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="caption" color="text.secondary">
                        {sentimentLoading && visibleStocks.length === 0
                          ? `Analyzing ${portfolioStocks.length} stocks...`
                          : `Loading stocks: ${visibleStocks.length} / ${sentiment?.stocks.length || 0}`}
                      </Typography>
                    </Box>
                    {loadingProgress > 0 && (
                      <Box
                        sx={{
                          width: '100%',
                          height: 4,
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${loadingProgress}%`,
                            height: '100%',
                            backgroundColor: '#2196f3',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </ListItem>
              )}

              {/* Empty state: No stocks with money flow data */}
              {!isRevealing && stocksWithMoneyFlow.length === 0 && visibleStocks.length > 0 && (
                <ListItem>
                  <Box sx={{ width: '100%', p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="warning.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <WarningIcon /> No Money Flow Data Available
                    </Typography>

                    {/* Data freshness indicators */}
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                      {lastRefreshed && (
                        <Chip
                          size="small"
                          icon={<InfoIcon />}
                          label={`Last refreshed: ${formatDataTimestamp(lastRefreshed)}`}
                          variant="outlined"
                          color="info"
                        />
                      )}
                      {moneyFlowHealth && (
                        <Chip
                          size="small"
                          label={`${Math.round(moneyFlowHealth.portfolioAvailability.percentage)}% availability`}
                          color={moneyFlowHealth.status === 'healthy' ? 'success' : moneyFlowHealth.status === 'degraded' ? 'warning' : 'error'}
                          variant="outlined"
                        />
                      )}
                      {moneyFlowHealth?.portfolioAvailability?.missingSymbols?.length > 0 && (
                        <Chip
                          size="small"
                          label={`${moneyFlowHealth.portfolioAvailability.missingSymbols.length} missing symbols`}
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {visibleStocks.length} stock{visibleStocks.length !== 1 ? 's' : ''} loaded but none have money flow indicators.
                    </Typography>

                    {/* Money Flow Pool Status */}
                    {systemHealth?.services?.moneyFlowPool && (
                      <Alert
                        severity={
                          systemHealth.services.moneyFlowPool.status === 'healthy' ? 'info' :
                          systemHealth.services.moneyFlowPool.status === 'degraded' ? 'warning' :
                          'error'
                        }
                        sx={{ mt: 2, mb: 2, textAlign: 'left' }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          Money Flow Pool Status: {systemHealth.services.moneyFlowPool.status.toUpperCase()}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          Completeness: {systemHealth.services.moneyFlowPool.completeness}% •
                          Fresh: {systemHealth.services.moneyFlowPool.fresh} •
                          Stale: {systemHealth.services.moneyFlowPool.stale} •
                          Missing: {systemHealth.services.moneyFlowPool.missing}
                        </Typography>
                        {systemHealth.services.moneyFlowPool.issues.length > 0 && (
                          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2, fontSize: '0.75rem' }}>
                            {systemHealth.services.moneyFlowPool.issues.map((issue: string, idx: number) => (
                              <li key={idx}><Typography variant="caption">{issue}</Typography></li>
                            ))}
                          </Box>
                        )}
                      </Alert>
                    )}

                    {/* Enhanced explanation with health data */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      <strong>Common causes:</strong> Weekend timing (cache runs Mon-Fri), Yahoo Finance rate limiting, or API issues.
                    </Typography>

                    {/* Health recommendations */}
                    {moneyFlowHealth?.recommendations && moneyFlowHealth.recommendations.length > 0 && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                        <Typography variant="caption" color="info.contrastText">
                          <strong>Recommendations:</strong>
                        </Typography>
                        {moneyFlowHealth.recommendations.slice(0, 2).map((rec: string, idx: number) => (
                          <Typography key={idx} variant="caption" color="info.contrastText" sx={{ display: 'block' }}>
                            • {rec}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {/* Weekend-specific information */}
                    {new Date().getDay() === 0 || new Date().getDay() === 6 ? (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                        <Typography variant="caption" color="warning.contrastText">
                          <strong>Weekend Notice:</strong> Cache warming runs Mon-Fri at 9am, 1pm, and 5pm Taiwan time. Weekend light refresh available at 12pm.
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                </ListItem>
              )}

              {/* Show filtered stocks with enhanced visual indicators */}
              {filteredStocks.map((stock, index) => {
                const quadrant = getQuadrant(stock);
                const isExpanded = expandedStock === stock.symbol;
                const priceChange = stock.changePercent;
                const PriceIcon =
                  priceChange > 0 ? TrendingUp : priceChange < 0 ? TrendingDown : TrendingFlat;
                const priceColor = priceChange > 0 ? '#4caf50' : priceChange < 0 ? '#f44336' : '#9e9e9e';
                const hasMoneyFlow = stock.moneyFlow !== null;

                return (
                  <Box
                    key={stock.symbol}
                    sx={{
                      animation: 'fadeIn 0.5s ease-in',
                      '@keyframes fadeIn': {
                        from: { opacity: 0, transform: 'translateY(-10px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                    }}
                  >
                    <ListItem disablePadding data-testid={`stock-item-${stock.symbol}`} sx={{ opacity: hasMoneyFlow ? 1 : 0.6 }}>
                      <Checkbox
                        edge="start"
                        checked={selectedStocks.has(stock.symbol)}
                        onChange={() => handleStockToggle(stock.symbol)}
                        tabIndex={-1}
                        disableRipple
                        sx={{ py: 0 }}
                        disabled={!hasMoneyFlow}
                      />
                      <ListItemButton onClick={() => handleStockClick(stock.symbol)}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: hasMoneyFlow ? (quadrant?.color || '#9e9e9e') : '#666',
                            borderRadius: '50%',
                            mr: 2,
                            flexShrink: 0,
                            border: hasMoneyFlow ? 'none' : '1px dashed #999',
                          }}
                        />
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight="bold">
                                {stock.symbol}
                                {!hasMoneyFlow && <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>⚠️</Typography>}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PriceIcon sx={{ fontSize: 16, color: priceColor }} />
                                <Typography variant="caption" sx={{ color: priceColor }}>
                                  {priceChange > 0 ? '+' : ''}
                                  {priceChange.toFixed(2)}%
                                </Typography>
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {hasMoneyFlow ? stock.name : `${stock.name} (no money flow)`}
                            </Typography>
                          }
                        />
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.3)' }}>
                        <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                          <strong>Price:</strong> ${stock.price.toFixed(2)} ({priceChange > 0 ? '+' : ''}
                          {priceChange.toFixed(2)}%)
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                          <strong>Signal:</strong>{' '}
                          <Chip
                            label={stock.signal}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.7rem',
                              backgroundColor:
                                stock.signal === 'STRONG_BUY'
                                  ? '#4caf50'
                                  : stock.signal === 'OPPORTUNITY'
                                  ? '#2196f3'
                                  : stock.signal === 'STRONG_SELL'
                                  ? '#f44336'
                                  : '#ff9800', // BULL_TRAP
                            }}
                          />
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                          <strong>Confidence:</strong> {stock.confidence}%
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                          <strong>Quadrant:</strong> {quadrant?.name || 'NEUTRAL'}
                        </Typography>
                        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
                        <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                          <strong>News Sentiment:</strong> {stock.distilbertSentiment.toFixed(2)} (
                          {stock.sentimentLabel})
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                          <strong>Money Flow:</strong>{' '}
                          {stock.moneyFlow ? (
                            `${stock.moneyFlow.cmf.toFixed(2)} (${stock.moneyFlow.trend})`
                          ) : (
                            <Box component="span" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                              ⚠️ NULL - Historical data missing
                            </Box>
                          )}
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                          <strong>AI Agreement:</strong> {stock.agreement}
                        </Typography>
                        <Typography variant="caption" component="div" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {stock.gptContext.length > 150
                            ? stock.gptContext.substring(0, 150) + '...'
                            : stock.gptContext}
                        </Typography>
                      </Box>
                    </Collapse>
                    <Divider />
                  </Box>
                );
              })}
            </List>
          </Box>

          {/* SVG Chart */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            {isRevealing && (
              <Box
                sx={{
                  position: 'absolute',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  zIndex: 10,
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  padding: 4,
                  borderRadius: 2,
                }}
              >
                <CircularProgress 
                  size={60} 
                  thickness={4}
                  sx={{
                    '& .MuiCircularProgress-circle': {
                      animation: 'colorShift 3s ease-in-out infinite',
                    },
                    '@keyframes colorShift': {
                      '0%': { stroke: '#4caf50' },   // Green (STRONG_BUY)
                      '25%': { stroke: '#2196f3' },  // Blue (OPPORTUNITY)
                      '50%': { stroke: '#f44336' },  // Red (STRONG_SELL)
                      '75%': { stroke: '#ff9800' },  // Orange (BULL_TRAP)
                      '100%': { stroke: '#4caf50' }, // Back to Green
                    },
                  }}
                />
                <Typography variant="h6" color="white">
                  {loadingMessage}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  AI sentiment analysis requires real-time processing
                </Typography>
              </Box>
            )}
            <Box sx={{ maxHeight: { xs: 280, sm: 400, md: 600 }, overflow: 'hidden' }}>
              <svg
                width="100%"
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                preserveAspectRatio="xMidYMid meet"
                style={{
                  border: '1px solid rgba(255,255,255,0.12)',
                  opacity: isRevealing ? 0.3 : 1,
                  maxWidth: chartWidth,
                }}
              >
            {/* Background quadrants */}
            <rect
              x={padding}
              y={padding}
              width={(chartWidth - 2 * padding) / 2}
              height={(chartHeight - 2 * padding) / 2}
              fill="rgba(33, 150, 243, 0.05)"
            />
            <rect
              x={chartWidth / 2}
              y={padding}
              width={(chartWidth - 2 * padding) / 2}
              height={(chartHeight - 2 * padding) / 2}
              fill="rgba(76, 175, 80, 0.05)"
            />
            <rect
              x={padding}
              y={chartHeight / 2}
              width={(chartWidth - 2 * padding) / 2}
              height={(chartHeight - 2 * padding) / 2}
              fill="rgba(244, 67, 54, 0.05)"
            />
            <rect
              x={chartWidth / 2}
              y={chartHeight / 2}
              width={(chartWidth - 2 * padding) / 2}
              height={(chartHeight - 2 * padding) / 2}
              fill="rgba(255, 152, 0, 0.05)"
            />

            {/* Axis lines */}
            <line
              x1={padding}
              y1={chartHeight / 2}
              x2={chartWidth - padding}
              y2={chartHeight / 2}
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="2"
            />
            <line
              x1={chartWidth / 2}
              y1={padding}
              x2={chartWidth / 2}
              y2={chartHeight - padding}
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="2"
            />

            {/* Axis labels */}
            <text
              x={chartWidth / 2}
              y={chartHeight - padding + 30}
              textAnchor="middle"
              fill="rgba(255,255,255,0.7)"
              fontSize="14"
            >
              News Sentiment →
            </text>
            <text
              x={padding - 40}
              y={chartHeight / 2}
              textAnchor="middle"
              fill="rgba(255,255,255,0.7)"
              fontSize="14"
              transform={`rotate(-90, ${padding - 40}, ${chartHeight / 2})`}
            >
              Money Flow (CMF) →
            </text>

            {/* Y-axis tick marks and labels (CMF scale) */}
            {[0.5, 0.25, 0, -0.25, -0.5].map((value) => {
              const yPos = chartHeight / 2 - (value / CMF_DISPLAY_MAX) * (chartHeight - 2 * padding) / 2;
              return (
                <g key={value}>
                  {/* Tick mark */}
                  <line
                    x1={padding - 5}
                    y1={yPos}
                    x2={padding}
                    y2={yPos}
                    stroke="rgba(255,255,255,0.3)"
                    strokeWidth="1"
                  />
                  {/* Label */}
                  <text
                    x={padding - 10}
                    y={yPos}
                    textAnchor="end"
                    fill="rgba(255,255,255,0.5)"
                    fontSize="10"
                    dominantBaseline="middle"
                  >
                    {value.toFixed(2)}
                  </text>
                  {/* Horizontal grid line */}
                  {value !== 0 && (
                    <line
                      x1={padding}
                      y1={yPos}
                      x2={chartWidth - padding}
                      y2={yPos}
                      stroke="rgba(255,255,255,0.05)"
                      strokeWidth="1"
                      strokeDasharray="2,2"
                    />
                  )}
                </g>
              );
            })}

            {/* Quadrant labels */}
            <text
              x={padding + (chartWidth - 2 * padding) / 4}
              y={padding + 20}
              textAnchor="middle"
              fill="rgba(255,255,255,0.5)"
              fontSize="12"
              fontWeight="bold"
            >
              Q2: OPPORTUNITY
            </text>
            <text
              x={chartWidth / 2 + (chartWidth - 2 * padding) / 4}
              y={padding + 20}
              textAnchor="middle"
              fill="rgba(255,255,255,0.5)"
              fontSize="12"
              fontWeight="bold"
            >
              Q1: STRONG_BUY
            </text>
            <text
              x={padding + (chartWidth - 2 * padding) / 4}
              y={chartHeight - padding - 10}
              textAnchor="middle"
              fill="rgba(255,255,255,0.5)"
              fontSize="12"
              fontWeight="bold"
            >
              Q3: STRONG_SELL
            </text>
            <text
              x={chartWidth / 2 + (chartWidth - 2 * padding) / 4}
              y={chartHeight - padding - 10}
              textAnchor="middle"
              fill="rgba(255,255,255,0.5)"
              fontSize="12"
              fontWeight="bold"
            >
              Q4: BULL_TRAP
            </text>

            {/* Plot stocks */}
            {stocksWithMoneyFlow
              .filter((stock) => selectedStocks.has(stock.symbol))
              .map((stock) => {
              const quadrant = getQuadrant(stock);

              // Map sentiment (-1 to +1) to x position
              const x = chartWidth / 2 + (stock.distilbertSentiment * (chartWidth - 2 * padding)) / 2;

              // Map money flow CMF to y position using industry standard range (-0.5 to +0.5)
              // Clamp CMF to display range, then scale to chart height (inverted: top = positive)
              const clampedCMF = Math.max(CMF_DISPLAY_MIN, Math.min(CMF_DISPLAY_MAX, stock.moneyFlow!.cmf));
              const y = chartHeight / 2 - (clampedCMF / CMF_DISPLAY_MAX) * (chartHeight - 2 * padding) / 2;

              // Use quadrant color if available, otherwise use gray for neutral stocks
              const fillColor = quadrant ? quadrant.color : '#9e9e9e';

              // Format tooltip content
              const sentimentLabel = stock.distilbertSentiment > 0 ? 'Bullish' : stock.distilbertSentiment < 0 ? 'Bearish' : 'Neutral';
              const moneyFlowLabel = stock.moneyFlow!.trend === 'ACCUMULATION' ? 'Accumulation' : stock.moneyFlow!.trend === 'DISTRIBUTION' ? 'Distribution' : 'Neutral';
              const tooltipText = `${stock.symbol} (${stock.name})
Sentiment: ${stock.distilbertSentiment.toFixed(2)} (${sentimentLabel})
Money Flow: ${stock.moneyFlow!.cmf.toFixed(2)} (${moneyFlowLabel})
Signal: ${stock.signal}
Confidence: ${stock.confidence}%
Quadrant: ${quadrant?.name || 'NEUTRAL'}`;

              return (
                <g key={stock.symbol}>
                  <circle
                    cx={x}
                    cy={y}
                    r="8"
                    fill={fillColor}
                    stroke="#fff"
                    strokeWidth="2"
                    data-symbol={stock.symbol}
                    data-quadrant={quadrant?.name || 'NEUTRAL'}
                  >
                    <title>{tooltipText}</title>
                  </circle>
                  <text
                    x={x}
                    y={y - 15}
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.9)"
                    fontSize="12"
                    fontWeight="bold"
                  >
                    {stock.symbol}
                  </text>
                </g>
              );
            })}
          </svg>
          </Box>
          </Box>
        </Box>

        {/* Stock Summary */}
        {sentiment && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Stock Distribution by Quadrant:
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Showing {selectedStocks.size} of {stocksWithMoneyFlow.length} stock{stocksWithMoneyFlow.length !== 1 ? 's' : ''} with money flow data
              {sentiment.stocks.length > stocksWithMoneyFlow.length && ` (${sentiment.stocks.length - stocksWithMoneyFlow.length} stocks without money flow data not displayed)`}
            </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            {['STRONG_BUY', 'OPPORTUNITY', 'STRONG_SELL', 'BULL_TRAP'].map((quadrantName) => {
              const stocksInQuadrant = stocksWithMoneyFlow.filter((s) => {
                const q = getQuadrant(s);
                return q?.name === quadrantName && selectedStocks.has(s.symbol);
              });

              const color =
                quadrantName === 'STRONG_BUY'
                  ? '#4caf50'
                  : quadrantName === 'OPPORTUNITY'
                  ? '#2196f3'
                  : quadrantName === 'STRONG_SELL'
                  ? '#f44336'
                  : '#ff9800';

              return (
                <Box key={quadrantName} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: color, borderRadius: 1 }} />
                  <Typography variant="caption">
                    <strong>{quadrantName}</strong>: {stocksInQuadrant.length} stock
                    {stocksInQuadrant.length !== 1 ? 's' : ''}
                  </Typography>
                  {stocksInQuadrant.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      ({stocksInQuadrant.map((s) => s.symbol).join(', ')})
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
          X-axis: News Sentiment (AI Analysis) • Y-axis: Money Flow (CMF Indicator)
        </Typography>
        {sentiment && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Data sourced: {formatDataTimestamp(sentiment.timestamp)}
            </Typography>
            {sentiment.cachedAt && (
              <Typography
                variant="caption"
                sx={{
                  color: sentiment.cacheStatus === 'HIT' ? '#4caf50' : sentiment.cacheStatus === 'STALE_REFRESH' ? '#ff9800' : '#9e9e9e',
                  fontWeight: 600
                }}
              >
                Cache: {sentiment.cacheStatus}
                {' '}({formatCacheTimestamp(sentiment.cachedAt)})
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
